import React from 'react';
import { Button, Checkbox, Flex, Radio, Text, useMantineTheme } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { FileData } from 'src/data-capture/hooks/file-uploads/types';

type SelectorComponent = typeof Checkbox | typeof Radio;

type SelectableItem<S extends string | number> = {
  id: S;
  title: string;
  file: FileData;
  description?: string;
  right?: string;
  emptyView?: React.ReactNode;
  disabled?: boolean;
};

type SelectableListRowProps<S extends string | number, T extends SelectableItem<S>> = {
  item: T;
  isSelected: boolean;
  isHovered: boolean;
  setHoveredId: (id: S | null) => void;
  onSelect: (item: T) => void;
  onViewFile: (file: FileData) => void;
  selector: SelectorComponent;
  showViewButton?: boolean;
  dateLayout?: 'inline' | 'stacked';
  highlighted?: boolean;
};

export function SelectableListRow<S extends string | number, T extends SelectableItem<S>>({
  item,
  isSelected,
  isHovered,
  setHoveredId,
  onSelect,
  onViewFile,
  selector: Selector,
  showViewButton = true,
  dateLayout = 'stacked',
  highlighted,
}: SelectableListRowProps<S, T>) {
  const theme = useMantineTheme();

  const backgroundColor = isSelected
    ? theme.colors.brand[0]
    : isHovered
      ? theme.colors.gray[0]
      : 'transparent';

  const formattedDate = formatUploadedDate(item.description);

  const borderColor = highlighted ? theme.colors.brand[6] : 'transparent';

  return (
    <Flex
      align="center"
      gap="md"
      px="md"
      py="sm"
      role="listitem"
      data-testid="selectable-list-item"
      onClick={() => onSelect(item)}
      onMouseEnter={() => setHoveredId(item.id)}
      onMouseLeave={() => setHoveredId(null)}
      opacity={item.disabled ? 0.6 : 1}
      style={{
        border: '1px solid',
        borderColor: borderColor,
        backgroundColor,
        borderRadius: 16,
        cursor: item.disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
    >
      <Selector
        checked={isSelected}
        disabled={item.disabled}
        color="brand"
        onClick={e => e.stopPropagation()}
        onChange={() => onSelect(item)}
      />

      <Flex align="center" flex={1} miw={0}>
        {dateLayout === 'inline' ? (
          <Flex align="center" flex={1} miw={0}>
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.8" truncate flex={1}>
              {item.title}
            </Text>
            {formattedDate && (
              <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" ml="md">
                Uploaded: {formattedDate}
              </Text>
            )}
          </Flex>
        ) : (
          <>
            <Flex direction="column" gap="xxxs" flex={1} miw={0}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.8" truncate>
                {item.title}
              </Text>

              {formattedDate && (
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" truncate>
                  {formattedDate}
                </Text>
              )}
            </Flex>

            <Flex align="center">
              {showViewButton && (
                <Button
                  size="sm"
                  fw={FONT_WEIGHTS.semibold}
                  color="orange.1"
                  c="brand.6"
                  onClick={e => {
                    e.stopPropagation();
                    onViewFile(item.file);
                  }}
                >
                  View
                </Button>
              )}
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
}

const formatUploadedDate = (description?: string) => {
  if (!description) return undefined;

  const match = description.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return undefined;

  const [, day, month, year] = match;

  const date = new Date(Number(year), Number(month) - 1, Number(day));

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};
